import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { CreatorSectionTab, CreatorTab, UploadPanelType } from "@/types";
import { creatorTabOptions } from "@/util";
import {
  createProjectApiStore,
  gitHubApiStore,
  identifierSaveStore,
  integrationsStore,
  jiraApiStore,
  projectSaveStore,
  useScreen,
} from "@/hooks";
import { getParam, QueryParams, Routes } from "@/router";
import {
  FlexBox,
  FlexItem,
  PanelCard,
  TextButton,
  TabList,
} from "@/components/common";
import { CreatorTree } from "@/components/graph";
import { ProjectIdentifierInput } from "@/components/project/save";
import { UploadPanelList } from "@/components/project/creator/upload";


/**
 * Builds project data uploads from all data sources.
 */
const __default__ = {
  name: "ProjectBuilder",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { smallWindow } = useScreen();
const currentRoute = useRoute();

const tabs = creatorTabOptions();
const tab = ref<CreatorSectionTab>("name");

const identifier = computed(() => identifierSaveStore.editedIdentifier);

const uploadMode = computed(() => projectSaveStore.mode);

const continueDisabled = computed(() => !identifierSaveStore.canSave);

const uploadDisabled = computed(
  () =>
    !projectSaveStore.uploadPanels
      .map(({ valid }) => valid)
      .reduce((acc, cur) => acc && cur, true) ||
    (!identifier.value.name && !["github", "jira"].includes(uploadMode.value))
);

const displayGraph = computed(() => projectSaveStore.graphNodes.length > 0);

const graphClassName = computed(() => {
  const graphClass = displayGraph.value
    ? "artifact-view visible q-ma-sm "
    : "artifact-view collapsed q-ma-sm ";
  const screenClass = smallWindow.value ? "full-width" : "";

  return graphClass + screenClass;
});

/**
 * Clears all project data.
 */
function handleClear() {
  identifierSaveStore.resetIdentifier(true);
  projectSaveStore.resetProject();
}

/**
 * Creates a new project.
 */
function handleCreate() {
  const callbacks = { onSuccess: handleClear };

  if (uploadMode.value === "bulk") {
    createProjectApiStore.handleBulkImport(
      identifier.value,
      projectSaveStore.uploadPanels[0]?.bulkFiles || [],
      projectSaveStore.uploadPanels[0]?.summarize || false,
      callbacks
    );
  } else if (uploadMode.value === "artifact" || uploadMode.value === "trace") {
    createProjectApiStore.handleImport(callbacks);
  } else if (uploadMode.value === "github") {
    createProjectApiStore.handleGitHubImport(callbacks);
  } else if (uploadMode.value === "jira") {
    createProjectApiStore.handleJiraImport(callbacks);
  }
}

/**
 * Reloads integrations data.
 */
function handleReloadIntegrations() {
  if (integrationsStore.validJiraCredentials) {
    jiraApiStore.handleLoadOrganizations();
  }
  if (integrationsStore.validGitHubCredentials) {
    gitHubApiStore.handleLoadProjects();
  }
}

/**
 * Reads the URL query for which tab to open to.
 */
function handleLoadTab() {
  const loadedToken = getParam(QueryParams.TAB);

  if (!loadedToken) return;

  const variant =
    (
      {
        standard: "artifact",
        bulk: "bulk",
        import: "github",
      } as Record<CreatorTab | string, UploadPanelType>
    )[String(loadedToken)] || "artifact";

  projectSaveStore.uploadPanels[0].variant = variant;

  tab.value = variant === "github" ? "data" : "name";
}

onMounted(() => handleReloadIntegrations());

watch(
  () => [
    integrationsStore.validJiraCredentials,
    integrationsStore.validGitHubCredentials,
  ],
  () => handleReloadIntegrations()
);

watch(
  () => currentRoute.path,
  (path) => {
    if (path !== Routes.PROJECT_CREATOR) return;

    handleClear();
    handleLoadTab();
  }
);

onMounted(handleLoadTab);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(FlexBox), {
    column: _unref(smallWindow),
    justify: "center"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(FlexItem), {
        parts: _unref(smallWindow) ? '12' : '6',
        class: _normalizeClass(_unref(smallWindow) ? 'full-width' : '')
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PanelCard), {
            class: "q-ma-sm",
            title: "Create New Project",
            subtitle: "Import collections of code files, documents, or other artifacts."
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FlexBox), {
                column: _unref(smallWindow),
                "full-width": ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TabList), {
                    modelValue: tab.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((tab).value = $event)),
                    tabs: _unref(tabs),
                    vertical: !_unref(smallWindow)
                  }, {
                    name: _withCtx(() => [
                      _createVNode(_unref(ProjectIdentifierInput), {
                        name: identifier.value.name,
                        "onUpdate:name": _cache[0] || (_cache[0] = ($event: any) => ((identifier.value.name) = $event)),
                        description: identifier.value.description,
                        "onUpdate:description": _cache[1] || (_cache[1] = ($event: any) => ((identifier.value.description) = $event))
                      }, null, 8, ["name", "description"]),
                      _createVNode(_unref(TextButton), {
                        block: "",
                        outlined: "",
                        disabled: continueDisabled.value,
                        label: "Import Data",
                        color: "primary",
                        class: "q-mt-md",
                        icon: "upload",
                        "data-cy": "button-continue-project",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (tab.value = 'data'))
                      }, null, 8, ["disabled"])
                    ]),
                    data: _withCtx(() => [
                      _createVNode(_unref(UploadPanelList)),
                      _createVNode(_unref(TextButton), {
                        disabled: uploadDisabled.value,
                        block: "",
                        label: "Create Project",
                        color: "primary",
                        class: "q-mt-md",
                        icon: "project-add",
                        "data-cy": "button-create-project",
                        onClick: handleCreate
                      }, null, 8, ["disabled"])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "tabs", "vertical"])
                ]),
                _: 1
              }, 8, ["column"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["parts", "class"]),
      _createVNode(_unref(FlexItem), {
        parts: _unref(smallWindow) ? '12' : '6',
        class: _normalizeClass(graphClassName.value)
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PanelCard), null, {
            default: _withCtx(() => [
              _createVNode(_unref(CreatorTree))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["parts", "class"])
    ]),
    _: 1
  }, 8, ["column"]))
}
}

})